import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OrganizationRoleResolver } from '@gcv/shared';
import { ArrowForwardIos, CheckCircle, Menu } from '@mui/icons-material';
import {
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import ApiAuth0Session, { GCApplicationAccess } from 'api/api-util/api-auth0-session';
import { ReactComponent as DropdownGrey } from 'assets/images/down-arrow-grey.svg';
import { ReactComponent as DropdownArrow } from 'assets/images/drop-down-arrow.svg';
import { ReactComponent as NotificationBell } from 'assets/images/notification-bell.svg';
import { ReactComponent as SettingsIcon } from 'assets/images/settings-icon.svg';
import { NAV_DRAWER_WIDTH } from 'domain/consts/ui';
import { useComponent } from 'hooks';
import { USER_KEY } from 'hooks/keys';
import { getCrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { getNotificationStore } from 'stores/NotificationStore';
import { getUserStore } from 'stores/UserStore';
import { AppBar, Badge, Banner, Button, Container, ContainerItem, Divider, Text } from 'ui';
import { NotificationsDrawer } from 'ui/systems';

interface Props extends Record<string, unknown> {
  toggleNavDrawer: () => void;
}

export const CrbAppBar: React.FC<Props> = useComponent((props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobilesm = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const dispensaryStore = getCrbDispensaryStore();
  const userStore = getUserStore();
  const notificationStore = getNotificationStore();

  const resolver = new OrganizationRoleResolver();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [companyMap, setCompanyMap] = React.useState<{ id: string; name: string }[]>([]);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = React.useState(false);

  const logout = () => {
    navigate('/logout', { replace: true });
  };

  React.useEffect(() => {
    setCompanyMap(
      Object.values(dispensaryStore.limitedDispensaries)
        .map((d) => {
          return { id: d.crbId, name: d.name };
        })
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
    );
  }, [dispensaryStore.dispensaries]);

  const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const changeOrg = (orgId: string) => {
    dispensaryStore.setCurrentDispensary(orgId);

    notificationStore.stopPolling();
    notificationStore.clearStore();
    notificationStore.getAllUserNotifications(orgId, userStore.user.id);
    notificationStore.startPolling(orgId, userStore.user.id);

    handlePopoverClose();

    // Todo: There is probably a cleaner way of doing this, perhaps within the routing logic

    //if user is currently on an onboarding page, redirect them to dashboard to prevent weird stuff
    if (window.location.href.indexOf('/secure/crb/onboarding') > -1) {
      navigate('/secure/crb/dashboard');
    }
    //if user is currently on users page, but does not have access, redirect them to dashboard
    if (
      window.location.href.indexOf('/secure/crb/users') > -1 &&
      !(
        resolver.userCanDoAction(dispensaryStore.currentDispensary.groups, userStore.user, 'user_add') ||
        resolver.userCanDoAction(dispensaryStore.currentDispensary.groups, userStore.user, 'user_remove')
      )
    ) {
      navigate('/secure/crb/dashboard');
    }
    // If user is currently on Questionnaires Inbox page, but does not have access, redirect them to Dashboard.
    if (
      window.location.href.indexOf('/secure/crb/questionnaires') > -1 &&
      !resolver.userCanDoAction(
        dispensaryStore.currentDispensary.groups,
        userStore.user,
        'questionnaire_complete'
      )
    ) {
      navigate('/secure/crb/dashboard');
    }
    //if user is currently on company profile page, but does not have access, redirect them to dashboard
    if (
      window.location.href.indexOf('/secure/crb/company') > -1 &&
      !resolver.userCanDoAction(
        dispensaryStore.currentDispensary.groups,
        userStore.user,
        'organizational_details_view'
      )
    ) {
      navigate('/secure/crb/dashboard');
    }
    // when we add the crbid to the above areas we will need to make sure we replace the crb for the above areas
    // we can proably reuse some of the code below
    const regex = /crb\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/;
    const parts = window.location.href.split(regex);
    if (parts && parts.length > 1) {
      parts[1] = orgId;
      const newUrl = parts.join('');
      const secureIndex = newUrl.indexOf('/secure');
      const finalUrl =
        newUrl.substring(secureIndex, secureIndex + 7) + '/crb' + newUrl.substring(secureIndex + 7);
      navigate(finalUrl);
    }

    window.localStorage.removeItem('hasDocumentRequirements');
  };

  const redirectToSsoLogin = () => {
    ApiAuth0Session.selectedApplication = GCApplicationAccess.All;
    navigate('/sso');
  };

  const switchToSupportLogin = () => {
    window.sessionStorage.removeItem('gcvSupportUserOrg');
    window.sessionStorage.removeItem('gcvSupportUserOrgType');
    navigate('/login/support');
  };

  const setUserCacheData = () => {
    // TODO - GS-16372
    userStore.user?.id && queryClient.setQueryData([USER_KEY, userStore.user.id], userStore.user);
  };

  /**
   * This is the dropdown that shows when a user clicks on their username in the top-right
   * Contain switch orgs, logout, siwtch to SSO, switch back to support buttons
   */
  const userPopoverOptions = () => {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Container column dataCy="muo-container" padding={0} width="260px" position="relative">
          <Container dataCy="muo-container-header" padding={0}>
            <ContainerItem dataCy="muo-user-info">
              <Avatar sx={{ mr: 1 }}>
                {userStore.user.firstName.slice(0, 1)}
                {userStore.user.lastName.slice(0, 1)}
              </Avatar>
            </ContainerItem>
            <ContainerItem>
              <Text content={userStore.user.email} dataCy="muo-user-email" type="body2" />
            </ContainerItem>
          </Container>
          <ContainerItem padding={0} width="100%">
            <Divider />
          </ContainerItem>
          <ContainerItem dataCy="muo-org-list" padding={0} width="100%">
            <List>
              {companyMap.map((company) => {
                return (
                  <ListItemButton
                    dense
                    key={company.id}
                    divider
                    onClick={() => changeOrg(company.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.primary.light,
                        color: (theme) => theme.palette.primary.main,

                        '& .MuiListItemText-root .MuiTypography-root > div': {
                          color: (theme) => `${theme.palette.primary.main} !important`
                        }
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Text
                          sx={{ wordWrap: 'break-word' }}
                          content={`${company.name}`}
                          dataCy="muo-org-list-name"
                        />
                      }
                    />
                    <ListItem secondaryAction alignItems="center" sx={{ pr: 0, width: '25px' }}>
                      {company.id === dispensaryStore.currentDispensary.id ? (
                        <CheckCircle color="primary" data-cy="muo-org-list-icon" />
                      ) : (
                        <ArrowForwardIos color="disabled" data-cy="muo-org-list-icon" />
                      )}
                    </ListItem>
                  </ListItemButton>
                );
              })}
            </List>
            {userStore.user.role === 'gcv_customer_support' && (
              <Box sx={{ width: '100%', position: 'sticky', bottom: '0px', padding: '8px' }}>
                <Button
                  fullWidth
                  color="primary-outlined"
                  label="Switch Orgs"
                  onClick={switchToSupportLogin}
                  variant="text"
                />
              </Box>
            )}
            {!userStore.user.gcd_user_created && userStore.user.pq_user_id && (
              <Box sx={{ width: '100%', position: 'sticky', bottom: '0px', padding: '8px' }}>
                <Button
                  fullWidth
                  color="primary-outlined"
                  label="Switch to PayQwick"
                  onClick={redirectToSsoLogin}
                  variant="text"
                />
              </Box>
            )}
            {userStore.user.gcd_user_created && (
              <Box sx={{ width: '100%', position: 'sticky', bottom: '0px', padding: '8px' }}>
                <Button
                  fullWidth
                  color="primary-outlined"
                  label="Switch to Green Check Direct"
                  onClick={redirectToSsoLogin}
                  variant="text"
                />
              </Box>
            )}
            <Box sx={{ width: '100%', position: 'sticky', bottom: '0px', padding: '8px' }}>
              <Button fullWidth color="default" label="Log Out" onClick={() => logout()} variant="text" />
            </Box>
          </ContainerItem>
        </Container>
      </Popover>
    );
  };

  if (isMobilesm) {
    return (
      <AppBar>
        <Box
          sx={{
            margin: '11px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          <IconButton disableRipple color="inherit" onClick={() => props.toggleNavDrawer()} size="large">
            <Menu data-cy="mobile-menu-icon" />
          </IconButton>
          <Link to="/secure/crb/dashboard" data-cy="logo-link" onClick={setUserCacheData}>
            <img src="/img/ic-gcv-mobile.svg" alt="Green Check Verified" height={'28px'} width={'52px'} />
          </Link>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ color: (theme) => theme.palette.text.secondary, pr: '23px', maxWidth: '50px' }}>
              <Badge badgeContent={notificationStore.notifications.length} color="primary">
                <NotificationBell
                  onClick={() => setNotificationDrawerOpen(!notificationDrawerOpen)}
                  style={{ cursor: 'pointer' }}
                />
              </Badge>
            </Box>
            <Container nowrap dataCy="mui-dropdown-container" onClick={handlePopoverClick} padding="0">
              <DropdownGrey />
            </Container>
          </Box>
        </Box>

        {userPopoverOptions()}

        <NotificationsDrawer
          drawerOpen={notificationDrawerOpen}
          setDrawerOpen={setNotificationDrawerOpen}
          org={dispensaryStore.currentDispensary}
        />
      </AppBar>
    );
  }

  return (
    <AppBar>
      <Box
        sx={{
          alignItems: 'center',
          borderRight: '1px solid #bdbdbd',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          width: isMobile ? 'auto' : NAV_DRAWER_WIDTH
        }}
      >
        {isMobile ? (
          <IconButton
            disableRipple
            color="inherit"
            onClick={() => props.toggleNavDrawer()}
            size="large"
            sx={{ mx: 2 }}
          >
            <Menu data-cy="mobile-menu-icon" />
          </IconButton>
        ) : (
          <Link to="/secure/crb/dashboard" data-cy="logo-link" onClick={setUserCacheData}>
            <img src="/img/logo-2.svg" alt="Green Check Verified" height={'22px'} width={'168px'} />
          </Link>
        )}
      </Box>

      <Banner />
      <Divider vertical />
      <Box sx={{ color: (theme) => theme.palette.text.secondary, pl: 2, pr: 1, maxWidth: '50px' }}>
        <Badge badgeContent={notificationStore.notifications.length} color="primary">
          <NotificationBell
            onClick={() => setNotificationDrawerOpen(!notificationDrawerOpen)}
            style={{ cursor: 'pointer' }}
          />
        </Badge>
      </Box>

      <Box sx={{ color: (theme) => theme.palette.text.secondary, pl: 1, pr: 2 }}>
        <SettingsIcon
          onClick={() => {
            setUserCacheData();
            navigate('/secure/crb/user/settings');
          }}
          style={{ cursor: 'pointer' }}
          data-cy="settings-button"
        />
      </Box>

      <Divider vertical />

      <Container
        nowrap
        align="center"
        dataCy="mui-dropdown-container"
        onClick={handlePopoverClick}
        width="275px"
        maxWidth="275px"
      >
        <ContainerItem grow>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
          >
            <Text
              inline
              sx={{
                wordWrap: 'break-word',
                maxWidth: '200px',
                display: 'inline-block',
                width: '160px',
                whiteSpace: 'nowrap',
                overflow: 'hidden !important',
                textOverflow: 'ellipsis'
              }}
              content={`${dispensaryStore.currentDispensary.name}`}
              dataCy="app-bar-dispensary-name"
            />
          </Box>
        </ContainerItem>
        <ContainerItem>
          <DropdownArrow />
        </ContainerItem>
      </Container>

      {userPopoverOptions()}

      <NotificationsDrawer
        setUserCacheData={setUserCacheData}
        drawerOpen={notificationDrawerOpen}
        setDrawerOpen={setNotificationDrawerOpen}
        org={dispensaryStore.currentDispensary}
      />
    </AppBar>
  );
});
